<template lang="pug">
	.easy-creative--music-selection.flex-col.space
		.flex-row.space.align-center
			.font-large {{$tCaption('Music selection')}}
		.actions-divider.full-width
		.tb-padding
			Loader(v-if="isLoading", :text="$tCaption('Loading') + '..'")
			.media-list.flex-row.space.align-center(v-else)
				.left.flex-row.arrow.align-center.center(v-if="pageIndex > 0", @click="gotoMediaPage(-1)")
					fas(icon="chevron-left")
				.flex-1.media-item.flex-col(v-for="item in visibleMedia", :class="{ active: selectedMediaId === item.Id }", @click="selectMedia(item)")
					.flex-row.flex-1.align-center.center
						.flex-row.align-center.center.play-button
							fas(:icon="!item.Id ? 'shuffle' : (loadingMediaId && loadingMediaId === item.Id ? 'spinner' : (playingItemId && playingItemId === item.Id ? 'stop' : 'play'))", :spin="loadingMediaId && loadingMediaId === item.Id")
						span {{item.Name}}
						.time.font-small(v-if="item.Length") {{$Data.secondsToDisplayValue(item.Length)}}
				.right.arrow.flex-row.align-center.center(v-if="pageIndex < maxPageIndex", @click="gotoMediaPage(1)")
					fas(icon="chevron-right")
		.flex-col.space-medium.full-width
			.actions-divider.full-width
			.flex-row.max-limited.navigation-row.justify-end
				.flex-row.space
					DxButton(:text="$tCaption('Back', '@actions')", @click="stop(); $emit('back');", :activeStateEnabled="false", :focusStateEnabled="false")
					DxButton(:text="$tCaption('Next', '@actions')", type="default", @click="stop(); $emit('next');", :activeStateEnabled="false", :focusStateEnabled="false", :disabled="!!isLoading")
</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import DxButton from 'devextreme-vue/button';
	import mediaHelper from './media-helper';

	export default {
		translationCategory: 'creative.EasyCreative',
		name: 'easy-creative-music-selection',
		props: ['info'],
		components: {
			DxButton, Loader
		},
		data() {
			return {
				isLoading: false,
				media: [],
				selectedMediaId: null,
				pageIndex: 0,
				pageSize: 3,
				playingItemId: null,
				loadingMediaId: null,
				initPlayingId: null
			};
		},
		computed: {
			visibleMedia() {
				return this.media.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize);
			},
			maxPageIndex() {
				return Math.ceil(this.media.length / this.pageSize) - 1;
			},
			selectedMedia() {
				return this.selectedMediaId && this.media.find(f => f.Id === this.selectedMediaId);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.easyCreativeMusicSelection = this;
		},
		destroyed() {
			this.stop();
			delete this.$lastLoadedAdjectives;
			delete this.$mediaData;
			delete this.$player;
		},
		methods: {
			selectMedia(media) {
				this.selectedMediaId = media.Id;
				this.playMedia(media);
			},
			stop() {
				if (this.$player) {
					this.$player.pause();
					delete this.$player;
				}
				this.playingItemId = null;
				this.initPlayingId = null;
			},
			async playMedia(media) {
				const isPlaying = this.playingItemId === media.Id;
				this.stop();
				
				if (!media.Url || isPlaying) return;

				if (!this.$mediaData) {
					this.$mediaData = {};
				}

				this.initPlayingId = media.Id;
				let data = this.$mediaData && this.$mediaData[media.Id];
				if (!data) {
					this.loadingMediaId = media.Id;
					data = await this.$Data.getBlob(media.Url, false, { contentType: this.$Data.getBase64ContentType(media.Url) }).finally(() => this.loadingMediaId = null);
					this.$mediaData[media.Id] = data;
				}
				
				if (this.initPlayingId !== media.Id) return;

				this.$player = new Audio();
				this.$player.src = data;
				this.$player.addEventListener('canplaythrough', () => this.playingItemId = media.Id);
				this.$player.addEventListener('ended', () => {
					this.playingItemId = null
					delete this.$player;
				});
				this.$player.play();
			},
			gotoMediaPage(direction) {
				this.pageIndex += direction;
			},
			async onActivate() {
				if (this.$lastLoadedAdjectives !== this.info.adjectives()?.join(',')) {
					await this.loadMedia();
				}

				if (this.info.skippedSteps.music) {
					this.$emit('next');
				}
			},
			async loadMedia() {
				const duration = this.info.scriptBuilder().duration;
				const targetLength = duration?.isFixed ? duration?.length : (this.info.scriptBuilder().getScriptDuration() * 1.5);

				if (__DEBUG__)
					console.debug('ECStep Music: Load media selection', { targetLength });

				this.isLoading++;
				this.$lastLoadedAdjectives = this.info.adjectives()?.join(',');
				this.selectedMediaId = null;
				this.pageIndex = 0;
				this.media = [{ Id: null, Name: this.$tCaption('Random') }];
				const suggestedMedia = await mediaHelper.findMedia(this.info.adjectives()?.join(','), { aimLength: targetLength }).finally(() => this.isLoading--);
				this.media.push(...suggestedMedia.filter(f => f.Length >= targetLength));
				const existingName = {};
				this.media.map(f => {
					if (existingName[f.Name]) {
						existingName[f.Name] += 1;
						f.Name = `${f.Name} ${existingName[f.Name]}`;
					} else {
						existingName[f.Name] = 1;
					}
				});

				if (__DEBUG__)
					console.debug('ECStep Music: Load media selection done', this.media?.length);
			},
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.easy-creative--music-selection {
		min-height: 300px;

		@media all and (min-width: 720px) {
			width: 720px;
		}

		.media-item {
			user-select: none;
			padding: 20px;
			background: var(--branding-button-bg);
			color: var(--branding-button-color);
			margin: 4px;
			border-radius: 4px;
			transition: 0.3s;
			cursor: pointer;
			min-height: 120px;
			position: relative;

			.time {
				position: absolute;
				bottom: 4px;
				right: 8px;
				color: var(--branding-button-color);
				opacity: 0.4;
				transition: 0.3s;
			}

			&:hover, &.active {
				background: var(--branding-active-bg);
				color: var(--branding-active-color);

				.time {
					color: var(--branding-active-color);
				}
			}
		}

		.media-list {
			position: relative;
			user-select: none;

			.arrow {
				cursor: pointer;
				padding: 10px;
				border-radius: 50px;
				background: rgba(0,0,0,0.025);
				color: var(--branding-color);
				width: 40px;
				height: 40px;

				&:hover {
					color: var(--branding-highlight-color);
				}
			}
		}

		.play-button {
			min-height: 30px;
			min-width: 30px;
			height: 30px;
			width: 30px;
			border-radius: 30px;
			margin: 4px;
		}
	}
</style>
